import { hasAny, nonEmptyBullets, fmtRange, getSectionMarkerAttrs } from "./utils";
import { contactItems, ContactBit } from "./ContactBits";

export default function CreativeTemplate({ resume }) {
  const { personal, summary, experience, education, skills, projects, certifications, languages } = resume;
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Sans', sans-serif",
        color: "#0a0a0a",
        background: "#ffffff",
        padding: "2cm",
        fontSize: 10,
        lineHeight: 1.5,
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      {/* Full-bleed red accent bar — sits above the paper's 2 cm text
          safe-area because CreativeTemplate is a `FULL_BLEED_TEMPLATE_IDS`
          member, so `.paper` has `padding: 0` and this abs-positioned
          strip lands on the physical paper edge. */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 8, background: "#FF2A00" }} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 32, marginTop: 12 }}>
        <div>
          <div data-resume-h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 900, fontSize: 30, letterSpacing: "-0.03em", lineHeight: 0.95, textTransform: "uppercase" }}>
            {(personal.fullName || "Your Name").split(" ").map((w, i) => (
              <div key={`w-${i}-${w}`}>{w}</div>
            ))}
          </div>
          {personal.title && (
            <div style={{ marginTop: 8, fontSize: 10, color: "#FF2A00", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {personal.title}
            </div>
          )}
          <div data-resume-contact="true" style={{ marginTop: 20 }}>
            <SidebarLabel>Contact</SidebarLabel>
            {contactItems(personal, { includeLinks: false }).map((it) => (
              <div key={it.key} style={{ marginBottom: 2 }}>
                <ContactBit Icon={it.Icon} value={it.value} size={10} color="#0a0a0a" />
              </div>
            ))}
          </div>
          {contactItems(personal, { includeContact: false }).length > 0 && (
            <div data-resume-contact="true" style={{ marginTop: 12 }}>
              <SidebarLabel>Links</SidebarLabel>
              {contactItems(personal, { includeContact: false }).map((it) => (
                <div key={it.key} style={{ marginBottom: 2 }}>
                  <ContactBit Icon={it.Icon} value={it.value} size={10} color="#0a0a0a" />
                </div>
              ))}
            </div>
          )}
          {skills?.length > 0 && (
            <div data-resume-skills="true" style={{ marginTop: 14 }}>
              <SidebarLabel>Skills</SidebarLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                {skills.map((s, i) => (
                  <span key={`${s}-${i}`} style={{ background: "#0a0a0a", color: "#ffffff", padding: "2px 6px", fontSize: 9 }}>{s}</span>
                ))}
              </div>
            </div>
          )}
          {hasAny(education, ["school", "degree"]) && (
            <div data-resume-education="true" style={{ marginTop: 14 }}>
              <SidebarLabel>Education</SidebarLabel>
              {education.map((ed, i) => (ed.school || ed.degree) && (
                <div key={ed.id || i} style={{ marginBottom: 6 }}>
                  <div style={{ fontWeight: 600 }}>{ed.school}</div>
                  <div style={{ color: "#525252", fontSize: 10 }}>{ed.degree}{ed.field ? `, ${ed.field}` : ""}</div>
                  <div style={{ color: "#737373", fontSize: 9.5 }}>{fmtRange(ed.startDate, ed.endDate)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          {summary && (
            <MainSection title="Professional Summary">
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, lineHeight: 1.4, fontWeight: 500 }}>{summary}</div>
            </MainSection>
          )}

          {hasAny(experience, ["company", "role"]) && (
            <MainSection title="Experience">
              {experience.map((e, i) => {
                const bullets = nonEmptyBullets(e.bullets);
                if (!e.company && !e.role && bullets.length === 0) return null;
                return (
                  <div key={e.id || i} data-page-item="true" style={{ marginBottom: 12, paddingLeft: 12, borderLeft: "2px solid #FF2A00" }}>
                    <div data-resume-jobtitle style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 12 }}>
                      {e.role}
                    </div>
                    <div style={{ color: "#525252", fontSize: 10 }}>
                      <span data-resume-company>{e.company}</span>{e.location ? <> · <span data-resume-location>{e.location}</span></> : ""} <span style={{ color: "#a3a3a3" }}>· <span data-resume-date>{fmtRange(e.startDate, e.endDate)}</span></span>
                    </div>
                    {bullets.length > 0 && (
                      <ul data-resume-workdesc style={{ margin: "4px 0 0 14px", padding: 0 }}>
                        {bullets.map((b, bi) => (
                          <li key={`${e.id || i}-b-${bi}`} style={{ marginBottom: 2, fontSize: 10 }}>{b}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </MainSection>
          )}

          {hasAny(projects, ["name"]) && (
            <MainSection title="Projects">
              {projects.map((p, i) => p.name && (
                <div key={p.id || i} style={{ marginBottom: 8 }}>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif" }}><span data-resume-projectname style={{ fontWeight: 700 }}>{p.name}</span> {p.link && <span style={{ fontWeight: 400, color: "#FF2A00", fontSize: 10 }}>· {p.link}</span>}</div>
                  {p.tech && <div style={{ fontSize: 9.5, color: "#737373" }}>{p.tech}</div>}
                  {p.description && <div style={{ fontSize: 10, marginTop: 2 }}>{p.description}</div>}
                </div>
              ))}
            </MainSection>
          )}

          {hasAny(certifications, ["name"]) && (
            <MainSection title="Certifications">
              {certifications.map((c, i) => c.name && (
                <div key={c.id || i} style={{ marginBottom: 3 }}>
                  <span style={{ fontWeight: 600 }}>{c.name}</span>
                  {c.issuer && <span style={{ color: "#525252" }}> — {c.issuer}</span>}
                  {c.date && <span style={{ color: "#a3a3a3" }}> · {c.date}</span>}
                </div>
              ))}
            </MainSection>
          )}

          {languages?.some((l) => l.name?.trim()) && (
            <MainSection title="Languages">
              {languages.map((l) => l.name?.trim() && (
                <div key={l.id} style={{ marginBottom: 3 }}>
                  <span style={{ fontWeight: 600 }}>{l.name}</span>
                  {l.level && <span style={{ color: "#525252" }}> — {l.level}</span>}
                </div>
              ))}
            </MainSection>
          )}
        </div>
      </div>
    </div>
  );
}

function SidebarLabel({ children }) {
  return (
    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8.5, letterSpacing: "0.22em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 4 }}>
      {children}
    </div>
  );
}

function MainSection({ title, children }) {
  return (
    <section {...getSectionMarkerAttrs(title)} style={{ marginBottom: 18 }}>
      <div data-resume-h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: 16, textTransform: "uppercase", letterSpacing: "-0.01em", marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </section>
  );
}
